import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../services/api';
import { getAllClasses, getAllSchools } from '../../services/dashboardService';
import SearchableSelect from '../SearchableSelect';
import useEffectiveCity from '../../hooks/useEffectiveCity';

/**
 * Configurações → Dados dos Alunos: a ficha completa dos alunos (todos os
 * campos do cadastro) em Excel ou CSV.
 *
 * Fica aqui, e não em Relatórios, porque são dados pessoais sensíveis
 * (CPF, NIS, endereço, saúde): Configurações é só do administrador, e o
 * backend também recusa qualquer outro perfil. Cada download fica
 * registrado na Auditoria.
 */

const ENDPOINT = '/admin/alunos-ficha-completa/';

/** Parâmetros da URL: só os filtros escolhidos, mais o formato. */
export const montarParametrosFicha = ({ cidadeId, escolaId, turmas = [], ano, formato = 'xlsx' }) => {
  const params = { formato };
  if (cidadeId) params.city = String(cidadeId);
  if (escolaId) params.school = String(escolaId);
  if (turmas.length) params.class_ids = turmas.map(String).join(',');
  if (ano) params.year = String(ano);
  return params;
};

export const baixarFichaCompleta = async (params) => {
  const url = new URL(`${API_BASE_URL}${ENDPOINT}`);
  Object.entries(params).forEach(([k, v]) => url.searchParams.append(k, v));
  const response = await fetch(url.toString(), { method: 'GET', credentials: 'include' });
  if (!response.ok) {
    let mensagem = '';
    try {
      const corpo = await response.json();
      mensagem = corpo.error || corpo.detail || '';
    } catch (e) {
      // Resposta sem JSON: fica só o status.
    }
    throw new Error(mensagem || `Erro ao exportar: ${response.status}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const match = disposition && disposition.match(/filename="(.+)"/);
  const filename = match ? match[1] : `alunos_ficha_completa.${params.formato}`;
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
  return filename;
};

const ordenarTurmas = (a, b) => (a.class_name || '').localeCompare(b.class_name || '', 'pt-BR', { numeric: true });

function DadosDosAlunos({ darkMode, cardClass, titleClass, mutedClass }) {
  const cidade = useEffectiveCity();
  const [escolas, setEscolas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [escolaId, setEscolaId] = useState('');
  const [ano, setAno] = useState('');
  const [marcadas, setMarcadas] = useState([]);
  const [formato, setFormato] = useState('xlsx');
  const [ciente, setCiente] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [mensagem, setMensagem] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setEscolaId('');
    setMarcadas([]);
    Promise.all([getAllSchools(cidade?.id), getAllClasses(cidade?.id)])
      .then(([listaEscolas, listaTurmas]) => {
        if (cancelado) return;
        setEscolas([...(listaEscolas || [])].sort((a, b) => (a.school || '').localeCompare(b.school || '', 'pt-BR')));
        setTurmas(listaTurmas || []);
        const anos = (listaTurmas || []).map((t) => t.school_year).filter(Boolean);
        setAno(anos.length ? String(Math.max(...anos)) : '');
      })
      .catch(() => { if (!cancelado) setMensagem({ ok: false, texto: 'Não foi possível carregar escolas e turmas.' }); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [cidade?.id]);

  const anos = useMemo(
    () => [...new Set(turmas.map((t) => t.school_year).filter(Boolean))].sort((a, b) => b - a),
    [turmas],
  );

  const turmasDaEscola = useMemo(() => turmas
    .filter((t) => String(t.id_school) === String(escolaId))
    .filter((t) => !ano || String(t.school_year) === String(ano))
    .sort(ordenarTurmas), [turmas, escolaId, ano]);

  // Trocar escola ou ano desmarca o que não está mais na lista.
  useEffect(() => {
    const visiveis = new Set(turmasDaEscola.map((t) => t.id));
    setMarcadas((atual) => atual.filter((id) => visiveis.has(id)));
  }, [turmasDaEscola]);

  const alternar = (id) => setMarcadas((atual) => (
    atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]
  ));
  const todasMarcadas = turmasDaEscola.length > 0 && marcadas.length === turmasDaEscola.length;

  const podeExportar = Boolean(escolaId) && ciente && !exportando;

  const exportar = async () => {
    setExportando(true);
    setMensagem(null);
    try {
      const nome = await baixarFichaCompleta(montarParametrosFicha({
        cidadeId: cidade?.id, escolaId, turmas: marcadas, ano, formato,
      }));
      setMensagem({ ok: true, texto: `Arquivo gerado: ${nome}. O download foi registrado na Auditoria.` });
    } catch (err) {
      setMensagem({ ok: false, texto: err.message || 'Não foi possível exportar.' });
    } finally {
      setExportando(false);
    }
  };

  const campo = `w-full px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode ? 'bg-slate-700/50 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-200 text-slate-700'
  }`;
  const rotulo = `block text-sm mb-1 ${mutedClass}`;
  const texto = darkMode ? 'text-slate-200' : 'text-slate-700';

  return (
    <div className="space-y-6">
      <div className={cardClass}>
        <h2 className={titleClass}>Ficha Completa dos Alunos</h2>
        <p className={`text-sm mb-4 ${mutedClass}`}>
          Exporta todos os dados do cadastro, um aluno por linha: matrícula, dados pessoais,
          documentos, endereço, filiação e responsável, deficiência/TEA/AEE, saúde, vacinação,
          dados socioeconômicos e transporte.
        </p>

        <div className={`mb-5 p-3 rounded-lg text-sm border ${
          darkMode ? 'bg-[#F9B047]/10 border-[#F9B047]/40 text-[#F9B047]' : 'bg-[#F9B047]/10 border-[#F9B047]/40 text-amber-800'
        }`}>
          <strong>Dados sensíveis (LGPD).</strong> O arquivo tem CPF, NIS, endereço e informações
          de saúde. Guarde em local seguro, compartilhe só com quem precisa e apague quando não
          for mais necessário. Cada exportação fica registrada na Auditoria com seu usuário.
        </div>

        {carregando ? (
          <p className={mutedClass}>Carregando escolas e turmas...</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className={rotulo}>Escola</label>
                <SearchableSelect
                  value={escolaId}
                  onChange={setEscolaId}
                  options={escolas.map((e) => ({ value: e.id, label: e.school }))}
                  emptyOptionLabel="Selecione a escola"
                  darkMode={darkMode}
                  className="w-full"
                />
              </div>
              <div>
                <label className={rotulo}>Ano letivo</label>
                <select value={ano} onChange={(e) => setAno(e.target.value)} className={campo}>
                  <option value="">Todos</option>
                  {anos.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>

            {escolaId && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className={rotulo}>
                    Turmas {marcadas.length ? `(${marcadas.length} marcada${marcadas.length > 1 ? 's' : ''})` : '(nenhuma marcada = todas da escola)'}
                  </span>
                  {turmasDaEscola.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setMarcadas(todasMarcadas ? [] : turmasDaEscola.map((t) => t.id))}
                      className="text-xs text-[#0974BA] hover:underline"
                    >
                      {todasMarcadas ? 'Desmarcar todas' : 'Marcar todas'}
                    </button>
                  )}
                </div>
                {turmasDaEscola.length === 0 ? (
                  <p className={`text-sm ${mutedClass}`}>Nenhuma turma desta escola no ano escolhido.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {turmasDaEscola.map((t) => (
                      <label
                        key={t.id}
                        className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer text-sm ${
                          marcadas.includes(t.id)
                            ? 'border-[#0974BA] bg-[#0974BA]/10'
                            : darkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={marcadas.includes(t.id)}
                          onChange={() => alternar(t.id)}
                          className="mt-0.5"
                        />
                        <span className={texto}>
                          {t.class_name}
                          <span className={`block text-xs ${mutedClass}`}>
                            {[t.grade, t.shift, t.school_year].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4">
              <span className={rotulo}>Formato</span>
              {[['xlsx', 'Excel (.xlsx)'], ['csv', 'CSV']].map(([valor, nome]) => (
                <label key={valor} className={`flex items-center gap-2 text-sm cursor-pointer ${texto}`}>
                  <input type="radio" name="formato-ficha" value={valor}
                    checked={formato === valor} onChange={() => setFormato(valor)} />
                  {nome}
                </label>
              ))}
            </div>

            <label className={`flex items-start gap-2 text-sm cursor-pointer ${texto}`}>
              <input type="checkbox" checked={ciente} onChange={(e) => setCiente(e.target.checked)} className="mt-0.5" />
              Estou ciente de que o arquivo contém dados pessoais sensíveis e me responsabilizo pelo seu uso.
            </label>

            <button
              type="button"
              onClick={exportar}
              disabled={!podeExportar}
              className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                podeExportar
                  ? 'bg-sideb-green hover:bg-sideb-green/80 text-white'
                  : darkMode ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {exportando ? 'Gerando...' : 'Exportar ficha completa'}
            </button>
            {!escolaId && <p className={`text-xs ${mutedClass}`}>Escolha a escola para exportar.</p>}
          </div>
        )}

        {mensagem && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${
            mensagem.ok
              ? darkMode ? 'bg-[#13983D]/20 text-[#13983D] border border-[#13983D]/50' : 'bg-[#13983D]/5 text-[#13983D] border border-[#13983D]/20'
              : darkMode ? 'bg-[#E83A3C]/20 text-[#E83A3C] border border-[#E83A3C]/50' : 'bg-[#E83A3C]/5 text-[#E83A3C] border border-[#E83A3C]/20'
          }`}>
            {mensagem.texto}
          </div>
        )}
      </div>
    </div>
  );
}

export default DadosDosAlunos;
